import { useEffect, useRef } from 'react';

interface UseDoubleEscapeOptions {
    onClose: () => void;
    onShowFeedback?: (timeWindow: number) => void;
    enabled?: boolean;
    timeWindow?: number;
}

export function useDoubleEscape({
    onClose,
    onShowFeedback,
    enabled = true,
    timeWindow = 1000,
}: UseDoubleEscapeOptions): void {
    const firstPressRef = useRef<boolean>(false);
    const timerRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        if (!enabled) {
            return;
        }

        const handleKeyDown = (event: KeyboardEvent): void => {
            if (event.key !== 'Escape') {
                return;
            }

            event.preventDefault();
            event.stopPropagation();

            if (!firstPressRef.current) {
                firstPressRef.current = true;
                onShowFeedback?.(timeWindow);

                timerRef.current = setTimeout(() => {
                    firstPressRef.current = false;
                }, timeWindow);
            } else {
                if (timerRef.current) {
                    clearTimeout(timerRef.current);
                    timerRef.current = null;
                }
                firstPressRef.current = false;
                onClose();
            }
        };

        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            if (timerRef.current) {
                clearTimeout(timerRef.current);
            }
        };
    }, [enabled, onClose, onShowFeedback, timeWindow]);
}
